import type { Messages } from "./messages";

export const de: Messages = {
  locale: "de-DE",
  name: "Deutsch",
  page: {
    title: "Planets",
    description:
      "Ein interaktives 3D-Modell des Sonnensystems mit der Sonne, den Planeten, den Zwergplaneten und ihren Monden.",
  },
  fatal: {
    skeleton: "Dem Seitengerüst fehlt #scene, #labels oder #overlay",
    webgl:
      "planets.kuffel.me braucht einen Browser mit WebGL 2. Chrome, Firefox, Safari 15+ und Edge unterstützen es.",
    startup: "Start fehlgeschlagen:\n{error}",
    renderLoop: "Darstellung fehlgeschlagen:\n{error}",
  },
  loading: "Texturen werden geladen {done}/{total}",
  update: { available: "Neue Version verfügbar", reload: "Neu laden", dismiss: "Schließen" },
  stats:
    "{fps} Bilder/s · langsamstes {slowest} ms · CPU {cpu} ms\n" +
    "{width}×{height} @{ratio} · {draws} Zeichenaufrufe\n" +
    "{textures} Texturen · {geometries} Geometrien\n" +
    "Kepler-Fehlschläge {misses} · nicht endlich {nonFinite}",
  statsCopy: { copy: "Kopieren", copied: "Kopiert", failed: "Kopieren fehlgeschlagen" },
  viewport: {
    lines:
      "Viewport {width}×{height} · sichtbar {visual} · Bildschirm {screenWidth}×{screenHeight}\n" +
      "sicherer Bereich {top} {right} {bottom} {left} · {mode}\n" +
      "vh {vh} · lvh {lvh} · dvh {dvh} · svh {svh} · body {body} · Canvas {canvas}",
    installed: "installiert",
    browser: "im Browser",
  },
  menu: {
    label: "Menü",
    open: "Menü öffnen",
    close: "Menü schließen",
    closePanel: "Schließen",
    tabs: { controls: "Steuerung", bodies: "Himmelskörper", help: "Hilfe" },
  },
  player: { play: "Abspielen", pause: "Pause" },
  controls: {
    time: "Zeit",
    now: "Jetzt",
    speed: "Simulationsgeschwindigkeit",
    resetView: "Ansicht zurücksetzen",
    view: "Ansicht",
    trueScale: "Echter Maßstab",
    labels: "Beschriftungen",
    orbits: "Umlaufbahnen",
    fullscreen: "Vollbild",
    language: "Sprache",
  },
  speed: {
    realTime: "Echtzeit",
    units: {
      year: { one: "{count} Jahr", other: "{count} Jahre" },
      month: { one: "{count} Monat", other: "{count} Monate" },
      day: { one: "{count} Tag", other: "{count} Tage" },
      hour: { one: "{count} Stunde", other: "{count} Stunden" },
      minute: { one: "{count} Minute", other: "{count} Minuten" },
      second: { one: "{count} Sekunde", other: "{count} Sekunden" },
    },
    short: { year: "J", month: "Mon", day: "d", hour: "h", minute: "min", second: "s" },
  },
  kinds: { star: "Stern", planet: "Planet", dwarf: "Zwergplanet", moon: "Mond" },
  subtitle: { ofSun: "{kind} der Sonne", of: "{kind} von {parent}" },
  info: {
    radius: "Radius",
    equatorialRadius: "Äquatorradius",
    equator: "Äquator",
    unknown: "unbekannt",
    day: "Tageslänge",
    axialTilt: "Achsneigung",
    moons: "Monde",
    showMoons: "Alle Monde zeigen ›",
    hours: "{count} h",
    days: { one: "{count} Tag", other: "{count} Tage" },
    years: { one: "{count} Jahr", other: "{count} Jahre" },
    retrograde: "{period}, rückläufig",
    year: "Umlaufzeit",
    period: "Umlaufzeit",
    tabs: { overview: "Übersicht", body: "Körper", orbit: "Umlauf" },
    tabsLabel: "Details",
    mass: "Masse",
    gravity: "Schwerkraft",
    density: "Dichte",
    temperature: "Temperatur",
    distance: "Abstand",
    eccentricity: "Exzentrizität",
    inclination: "Bahnneigung",
    inclinationEquator: "Bahnneigung (zum Äquator)",
    inclinationLaplace: "Bahnneigung (zur Laplace-Ebene)",
    speed: "Geschwindigkeit",
    about: "etwa {value}",
    galacticOrbit: "um das Zentrum der Galaxis",
    auDistance: "{au} AE ({millionKm} Mio. km)",
    lightYears: { one: "{count} Lichtjahr", other: "{count} Lichtjahre" },
    millionYears: { one: "{count} Million Jahre", other: "{count} Millionen Jahre" },
  },
  bodies: {
    search: "{count} Himmelskörper durchsuchen…",
    searchLabel: "Himmelskörper suchen",
    moons: { one: "{count} Mond", other: "{count} Monde" },
    showMoons: "Monde von {name} zeigen",
    hideMoons: "Monde von {name} ausblenden",
    more: "+{count} weitere, zum Suchen tippen",
    noMatch: "Kein Himmelskörper namens „{query}“",
    focus: "Zu {name} fliegen",
  },
  help: {
    keyboard: "Tastatur",
    advanced: "Erweitert",
    stats: "Leistungsstatistik",
    about: "Über",
    credits:
      "Bahndaten: NASA/JPL Solar System Dynamics. Texturen: {scope} ({license}; die Karten der Zwergplaneten sind erfunden).",
    keyNames: { Space: "Leertaste" },
    keys: {
      pause: "Abspielen / Pause",
      faster: "Schneller",
      slower: "Langsamer",
      scale: "Echter Maßstab",
      labels: "Beschriftungen",
      orbits: "Umlaufbahnen",
      home: "Zurück zur Sonne",
      escape: "Menü oder Infokarte schließen, sonst zurück zur Sonne",
      menu: "Menü",
      search: "Himmelskörper suchen",
      help: "Tastenkürzel",
      stats: "Leistungsstatistik",
    },
  },
  bodyNames: {
    sun: "Sonne",
    mercury: "Merkur",
    earth: "Erde",
    neptune: "Neptun",
    moon: "Mond",
    ganymede: "Ganymed",
    callisto: "Kallisto",
  },
};
